import { Building2, ChevronDown, Copy, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '~/components/ui/dropdown-menu';
import { STOREFRONT_URL } from '~/lib/brand';

function hostname(url: string): string {
	try {
		return new URL(url).hostname.replace(/^www\./, '');
	} catch {
		return url;
	}
}

/**
 * Single-site context menu (not a multi-site switcher — deployment is single-site).
 * Opens the storefront in a new tab or copies its URL.
 */
export function SiteContextMenu() {
	const { t } = useTranslation();
	const storefrontUrl = STOREFRONT_URL;
	// Tenant without a configured storefront → render nothing.
	if (!storefrontUrl) return null;
	const domain = hostname(storefrontUrl);

	const openStore = () => window.open(storefrontUrl, '_blank', 'noopener,noreferrer');
	const copyUrl = async () => {
		try {
			await navigator.clipboard.writeText(storefrontUrl);
			toast.success(t('shell.site.copied'));
		} catch {
			toast.error(t('shell.site.copyFailed'));
		}
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger className="flex h-[30px] items-center gap-1.5 rounded-md px-2 text-foreground-muted transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none">
				<Building2 size={14} />
				<span className="text-[11.5px]">{domain}</span>
				<ChevronDown size={11} className="text-foreground-subtle" />
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="min-w-[200px]">
				<DropdownMenuItem onSelect={openStore}>
					<ExternalLink size={14} />
					<span>{t('shell.site.open')}</span>
				</DropdownMenuItem>
				<DropdownMenuItem onSelect={copyUrl}>
					<Copy size={14} />
					<span>{t('shell.site.copy')}</span>
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
